<template>
  <div>
    <v-autocomplete
      ref="autocomplete"
      v-model="selectedIds"
      v-model:search="search"
      :items="items"
      :loading="loading"
      :item-title="itemTitle"
      item-value="id"
      multiple
      chips
      closable-chips
      clearable
      hide-no-data
      hide-selected
      :return-object="false"
      density="comfortable"
      variant="outlined"
      placeholder="Введите минимум 2 символа"
      no-filter
      :menu-props="{ maxHeight: 360 }"
      @update:model-value="onSelectionChanged"
    >
      <template v-slot:chip="{ props: chipProps, item }">
        <v-chip
          v-bind="chipProps"
          size="small"
          :prepend-icon="platformIcon(item.raw)"
          :text="itemTitle(item.raw)"
        />
      </template>

      <template v-slot:item="{ props: itemProps, item }">
        <v-list-item v-bind="itemProps" :title="undefined">
          <template v-slot:prepend>
            <v-icon :icon="platformIcon(item.raw)" size="small" />
          </template>
          <v-list-item-title>
            {{ item.raw.display_name || item.raw.identifier }}
            <v-chip
              v-if="item.raw.source_type === 'search_query'"
              size="x-small"
              color="primary"
              variant="tonal"
              class="ml-1"
            >
              поиск
            </v-chip>
          </v-list-item-title>
          <v-list-item-subtitle>{{ item.raw.identifier }}</v-list-item-subtitle>
        </v-list-item>
      </template>

      <template v-slot:no-data>
        <v-list-item>
          <v-list-item-title class="text-medium-emphasis">
            <template v-if="!search || search.length < 2">
              Введите минимум 2 символа для поиска
            </template>
            <template v-else-if="loading">Загрузка…</template>
            <template v-else>Ничего не найдено</template>
          </v-list-item-title>
        </v-list-item>
      </template>
    </v-autocomplete>

    <div class="d-flex align-center mt-1">
      <span class="text-caption text-medium-emphasis">
        Выбрано: {{ selectedIds.length }}
      </span>
      <v-spacer />
      <v-btn
        v-if="selectedIds.length"
        size="x-small"
        variant="text"
        @click="clearAll"
      >
        Очистить
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted } from 'vue';
import apiClient from '@/apiClient';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
});
const emit = defineEmits(['update:modelValue']);

const autocomplete = ref(null);
const selectedIds = ref([...(props.modelValue || [])]);
const search = ref('');
const loading = ref(false);

// Pool of fully-hydrated source objects we've seen (selected + recent search hits).
// Keeping selected items here independently of `searchItems` guarantees their chips
// keep their label even after the user types a new search term.
const selectedItems = ref([]);
const searchItems = ref([]);
const items = ref([]);

const rebuildItems = () => {
  const seen = new Set();
  const merged = [];
  for (const it of [...selectedItems.value, ...searchItems.value]) {
    if (!it || seen.has(it.id)) continue;
    seen.add(it.id);
    merged.push(it);
  }
  items.value = merged;
};

const itemTitle = (raw) => {
  if (!raw || typeof raw !== 'object') return '';
  return raw.display_name || raw.identifier || `#${raw.id}`;
};

const platformIcon = (raw) => {
  const name = (raw?.platform_name || '').toLowerCase();
  if (name.includes('twitter') || name === 'x.com') return 'mdi-twitter';
  if (name.includes('telegram')) return 'mdi-send';
  if (name.includes('dzen')) return 'mdi-newspaper-variant';
  return 'mdi-link-variant';
};

watch(selectedIds, (val) => {
  const next = val || [];
  const prev = props.modelValue || [];
  if (next.length !== prev.length || next.some((id, i) => id !== prev[i])) {
    emit('update:modelValue', [...next]);
  }
});

watch(
  () => props.modelValue,
  (val) => {
    const next = val || [];
    if (
      next.length !== selectedIds.value.length ||
      next.some((id, i) => id !== selectedIds.value[i])
    ) {
      selectedIds.value = [...next];
      hydrateSelected();
    }
  },
);

// Promote anything currently selected from searchItems into selectedItems so
// the chip survives the next search.
const promoteSelectedFromSearch = () => {
  if (!selectedIds.value.length) return;
  const knownIds = new Set(selectedItems.value.map((it) => it.id));
  for (const id of selectedIds.value) {
    if (knownIds.has(id)) continue;
    const found = searchItems.value.find((it) => it.id === id);
    if (found) {
      selectedItems.value.push(found);
      knownIds.add(id);
    }
  }
};

// Called after the model changes (user picked or removed an item).
// Clear input + refocus, mirroring the AdminX tag-input UX.
const onSelectionChanged = async () => {
  promoteSelectedFromSearch();
  rebuildItems();
  search.value = '';
  searchItems.value = [];
  await nextTick();
  try { autocomplete.value?.focus?.(); } catch (_e) { /* ignore */ }
};

// Hydrate selected ids → fetch full objects so chips render with proper labels.
const hydrateSelected = async () => {
  const idsNeeded = selectedIds.value.filter(
    (id) => !selectedItems.value.some((it) => it.id === id),
  );
  if (!idsNeeded.length) {
    rebuildItems();
    return;
  }
  try {
    const { data } = await apiClient.get('/user-sources', {
      params: { ids: idsNeeded.join(',') },
    });
    const fetched = Array.isArray(data?.items) ? data.items : [];
    const byId = new Map(selectedItems.value.map((it) => [it.id, it]));
    for (const it of fetched) byId.set(it.id, it);
    selectedItems.value = Array.from(byId.values());
    rebuildItems();
  } catch (e) {
    console.error('Failed to hydrate selected user sources:', e);
  }
};

// Debounced server-side search
let searchTimer = null;
watch(search, (val) => {
  const term = (val || '').trim();
  if (searchTimer) clearTimeout(searchTimer);
  if (term.length < 2) {
    searchItems.value = [];
    rebuildItems();
    return;
  }
  searchTimer = setTimeout(async () => {
    loading.value = true;
    try {
      const { data } = await apiClient.get('/user-sources', {
        params: { q: term, limit: 50 },
      });
      searchItems.value = Array.isArray(data?.items) ? data.items : [];
      rebuildItems();
    } catch (e) {
      console.error('User-source search failed:', e);
      searchItems.value = [];
    } finally {
      loading.value = false;
    }
  }, 250);
});

const clearAll = () => {
  selectedIds.value = [];
};

onMounted(hydrateSelected);
</script>
